import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Briefcase, Zap, CalendarDays, Star, MessageSquare, ArrowLeft, UserPlus, UserCheck, Users, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import FriendsSection from '../components/FriendsSection';
import ProfileCapabilities from '../components/ProfileCapabilities';
import ProfilePortfolio from '../components/ProfilePortfolio';
import ComplianceWidget from '../components/ComplianceWidget';
import AccountingDocuments from '../components/AccountingDocuments';
import { ReputationBadge, BadgeChips, TrustedSellerBadge } from '../components/ReputationBadge';
import { resolveAssetUrl } from '../lib/assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FONT_MAP = {
  outfit: "'Outfit', sans-serif", figtree: "'Figtree', sans-serif", inter: "'Inter', sans-serif",
  playfair: "'Playfair Display', serif", merriweather: "'Merriweather', serif", montserrat: "'Montserrat', sans-serif",
};
const TEMPLATE_CSS = {
  minimalist: '#kolv-shop h1,#kolv-shop h2{letter-spacing:-.02em;font-weight:600}#kolv-shop *{box-shadow:none!important}',
  bold: '#kolv-shop h1{font-weight:800;letter-spacing:-.03em;text-transform:uppercase}',
  vintage: '#kolv-shop{font-family:"Merriweather",serif}#kolv-shop h1,#kolv-shop h2{font-family:"Playfair Display",serif}',
  craft: '#kolv-shop .rounded-xl,#kolv-shop .rounded-2xl{border-radius:20px}',
  modern: '#kolv-shop .rounded-xl{border-radius:6px}#kolv-shop h1{font-weight:700}',
};

const REGION_NAMES = {
  riga: 'Rīga', jurmala: 'Jūrmala', liepaja: 'Liepāja', ventspils: 'Ventspils',
  daugavpils: 'Daugavpils', jelgava: 'Jelgava', valmiera: 'Valmiera', cesis: 'Cēsis',
  sigulda: 'Sigulda', ogre: 'Ogre', tukums: 'Tukums',
};

export default function ProProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user: currentUser } = useAuth();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [custom, setCustom] = useState(null);
  const [friendStatus, setFriendStatus] = useState('none');
  const [friends, setFriends] = useState([]);
  const [friendBusy, setFriendBusy] = useState(false);
  const [ownerTab, setOwnerTab] = useState('roles');
  const isOwner = currentUser && currentUser._id === userId;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pro/${userId}`);
      setData(data);
      try {
        const cr = await axios.get(`${API}/shop-customization/${userId}`);
        if (cr.data && cr.data.apply_to_profile) setCustom(cr.data);
        else setCustom(null);
      } catch (e) { setCustom(null); }
      try {
        const fo = await axios.get(`${API}/friends/of/${userId}`);
        setFriends(fo.data.friends || []);
      } catch (e) { setFriends([]); }
      if (currentUser && currentUser._id !== userId) {
        try {
          const fs = await axios.get(`${API}/friends/status/${userId}`, { withCredentials: true });
          setFriendStatus(fs.data.status || 'none');
        } catch (e) { setFriendStatus('none'); }
      }
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId, currentUser]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const friendAction = async (kind) => {
    if (!currentUser) { navigate('/login'); return; }
    setFriendBusy(true);
    try {
      let res;
      if (kind === 'request') res = await axios.post(`${API}/friends/request/${userId}`, {}, { withCredentials: true });
      else if (kind === 'accept') res = await axios.post(`${API}/friends/${userId}/accept`, {}, { withCredentials: true });
      else if (kind === 'decline') res = await axios.post(`${API}/friends/${userId}/decline`, {}, { withCredentials: true });
      else if (kind === 'remove') res = await axios.delete(`${API}/friends/${userId}`, { withCredentials: true });
      const st = res?.data?.status || 'none';
      setFriendStatus(st);
      if (st === 'friends') { toast.success(tr('Tagad esat draugi!', 'You are now friends!', 'Теперь вы друзья!')); fetchData(); }
      else if (st === 'pending_sent') toast.success(tr('Uzaicinājums nosūtīts', 'Request sent', 'Заявка отправлена'));
      else if (st === 'none') toast.success(tr('Atjaunināts', 'Updated', 'Обновлено'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr('Neizdevās', 'Failed', 'Ошибка'));
    } finally { setFriendBusy(false); }
  };

  useEffect(() => {
    const ff = custom?.font_family;
    if (!ff || ff === 'outfit') return;
    const map = {
      playfair: 'Playfair+Display:wght@400;600;700', merriweather: 'Merriweather:wght@400;700',
      montserrat: 'Montserrat:wght@400;600;700', figtree: 'Figtree:wght@400;600;700', inter: 'Inter:wght@400;600;700',
    };
    const q = map[ff]; if (!q) return;
    const id = `pro-font-${ff}`; if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id; link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${q}&display=swap`;
    document.head.appendChild(link);
  }, [custom?.font_family]);

  if (loading) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D97736]" /></div>;
  }
  if (!data) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <p className="text-[#5C5C5C]">{tr('Profils nav atrasts.', 'Profile not found.', 'Профиль не найден.')}</p></div>;
  }

  const { user, portfolio, reviews, reputation } = data;
  const rep = reputation;
  const roles = [
    user.is_provider && { icon: Briefcase, lv: 'Sniedzējs', en: 'Provider', ru: 'Поставщик' },
    user.is_gig_worker && { icon: Zap, lv: 'Gig strādnieks', en: 'Gig worker', ru: 'Исполнитель' },
    user.is_organizer && { icon: CalendarDays, lv: 'Organizators', en: 'Organizer', ru: 'Организатор' },
  ].filter(Boolean);

  const cz = custom || {};
  const profileStyle = custom ? {
    backgroundColor: cz.bg_color || '#FAFAFA', color: cz.text_color || '#121212',
    fontFamily: FONT_MAP[cz.font_family] || FONT_MAP.outfit,
  } : {};
  const combinedCss = custom ? `${cz.template ? (TEMPLATE_CSS[cz.template] || '') : ''}\n${cz.custom_css || ''}` : '';

  return (
    <div id="kolv-shop" className={custom ? 'min-h-screen' : 'min-h-screen bg-[#FAFAFA]'} style={profileStyle} data-testid="pro-profile-page">
      {combinedCss.trim() && <style dangerouslySetInnerHTML={{ __html: combinedCss }} />}
      {cz.banner_top_html && <div data-testid="pro-banner-top" dangerouslySetInnerHTML={{ __html: cz.banner_top_html }} />}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#5C5C5C] hover:text-[#D97736] mb-6 text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Atpakaļ', 'Back', 'Назад')}
        </button>

        {/* Header */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-[#D97736] flex items-center justify-center text-white text-3xl font-bold shrink-0">
              {user.avatar_url ? <img src={resolveAssetUrl(user.avatar_url)} alt={user.name} className="w-full h-full rounded-full object-cover" /> : user.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-outfit text-2xl font-bold text-[#121212]">{user.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-[#5C5C5C]">
                {user.region && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{REGION_NAMES[user.region] || user.region}</span>}
              </div>
              <div className="mt-3">
                <ReputationBadge level={rep.level} levelInfo={rep.level_info} avgRating={rep.avg_rating} reviewCount={rep.review_count} />
              </div>
              {rep.trusted_seller && (
                <div className="mt-2" data-testid="profile-trusted-seller">
                  <TrustedSellerBadge />
                </div>
              )}
              {roles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {roles.map((r, i) => {
                    const Icon = r.icon;
                    return <span key={i} className="inline-flex items-center gap-1 text-xs bg-[#F3F2EE] text-[#5C5C5C] px-3 py-1 rounded-full">
                      <Icon className="w-3.5 h-3.5" />{r[language] || r.lv}</span>;
                  })}
                </div>
              )}
              {(user.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="pro-profile-tags">
                  {user.tags.map((t) => (
                    <span key={t} className="inline-flex items-center text-xs bg-[#FFF6F0] text-[#D97736] font-medium px-3 py-1 rounded-full" data-testid={`pro-tag-${t}`}>#{t}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button onClick={() => navigate(`/messages?to=${userId}&name=${encodeURIComponent(user.name || '')}`)} className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="contact-btn">
                <MessageSquare className="w-4 h-4 mr-1" /> {tr('Rakstīt ziņu', 'Send message', 'Написать')}
              </Button>
              {currentUser && currentUser._id !== userId && (
                friendStatus === 'friends' ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={friendBusy} className="border-[#4A6B53] text-[#4A6B53]" data-testid="friend-status-btn">
                        <UserCheck className="w-4 h-4 mr-1" /> {tr('Draugi', 'Friends', 'Друзья')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="remove-friend-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{tr('Noņemt no draugiem?', 'Remove friend?', 'Удалить из друзей?')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {tr(
                            `Vai tiešām noņemt ${user.name || 'šo lietotāju'} no draugiem? Draudzība tiks pārtraukta abiem.`,
                            `Are you sure you want to remove ${user.name || 'this user'} from friends? The friendship ends for both of you.`,
                            `Удалить ${user.name || 'этого пользователя'} из друзей? Дружба прекратится у обоих.`
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="remove-friend-cancel">{tr('Atcelt', 'Cancel', 'Отмена')}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => friendAction('remove')} className="bg-[#C0392B] hover:bg-[#A93226] text-white" data-testid="remove-friend-confirm">
                          {tr('Noņemt', 'Remove', 'Удалить')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : friendStatus === 'pending_sent' ? (
                  <Button variant="outline" disabled className="border-[#E5E4E0] text-[#9CA3AF]" data-testid="friend-status-btn">
                    {tr('Gaida apstiprinājumu', 'Request pending', 'Ожидает')}
                  </Button>
                ) : friendStatus === 'pending_received' ? (
                  <div className="flex gap-2">
                    <Button onClick={() => friendAction('accept')} disabled={friendBusy} className="bg-[#4A6B53] hover:bg-[#3d5945] text-white flex-1" data-testid="accept-friend-btn">
                      <Check className="w-4 h-4 mr-1" /> {tr('Pieņemt', 'Accept', 'Принять')}
                    </Button>
                    <Button variant="outline" onClick={() => friendAction('decline')} disabled={friendBusy} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid="decline-friend-btn">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => friendAction('request')} disabled={friendBusy} className="border-[#D97736] text-[#D97736] hover:bg-[#FFF6F0]" data-testid="add-friend-btn">
                    <UserPlus className="w-4 h-4 mr-1" /> {tr('Uzaicināt draugos', 'Add friend', 'Добавить в друзья')}
                  </Button>
                )
              )}
            </div>
          </div>
          {user.bio && <p className="text-[#5C5C5C] mt-5 font-figtree">{user.bio}</p>}

          {/* Stats + level progress */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-[#F3F2EE]">
            <div className="text-center">
              <p className="text-2xl font-bold text-[#121212] font-outfit">{rep.completed_gigs}</p>
              <p className="text-xs text-[#5C5C5C]">{tr('Pabeigti darbi', 'Completed gigs', 'Завершено')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#121212] font-outfit">{rep.avg_rating || '—'}</p>
              <p className="text-xs text-[#5C5C5C]">{tr('Vidējais reitings', 'Avg rating', 'Рейтинг')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#121212] font-outfit">{rep.review_count}</p>
              <p className="text-xs text-[#5C5C5C]">{tr('Atsauksmes', 'Reviews', 'Отзывы')}</p>
            </div>
          </div>
          {rep.next_level && (
            <div className="mt-5" data-testid="level-progress">
              <div className="flex justify-between text-xs text-[#5C5C5C] mb-1">
                <span>{tr('Līdz nākamajam līmenim', 'To next level', 'До следующего уровня')}: {rep.next_level[language] || rep.next_level.lv}</span>
                <span>{rep.level_progress}%</span>
              </div>
              <Progress value={rep.level_progress} className="h-2" />
            </div>
          )}
        </div>

        {/* Badges */}
        {rep.badges?.length > 0 && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 mb-6">
            <h2 className="font-outfit font-semibold text-[#121212] mb-4">{tr('Nozīmītes', 'Badges', 'Значки')}</h2>
            <BadgeChips badges={rep.badges} />
          </div>
        )}

        {/* Owner dashboard — management sections moved here from /profile */}
        {isOwner && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-2 mb-6" data-testid="owner-dashboard">
            <div className="flex gap-1 border-b border-[#F3F2EE] mb-4 overflow-x-auto">
              {[
                { id: 'roles', lv: 'Lomas', en: 'Roles', ru: 'Роли' },
                { id: 'accounting', lv: 'Statuss un grāmatvedība', en: 'Status & accounting', ru: 'Статус и учёт' },
                { id: 'friends', lv: 'Draugi', en: 'Friends', ru: 'Друзья' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setOwnerTab(tab.id)}
                  className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${ownerTab === tab.id ? 'border-[#D97736] text-[#D97736]' : 'border-transparent text-[#5C5C5C] hover:text-[#121212]'}`}
                  data-testid={`owner-tab-${tab.id}`}
                >
                  {tab[language] || tab.lv}
                </button>
              ))}
            </div>
            <div className="p-3 space-y-6">
              {ownerTab === 'roles' && (
                <>
                  <ProfileCapabilities />
                  {currentUser.is_provider && <ProfilePortfolio />}
                </>
              )}
              {ownerTab === 'accounting' && (
                <>
                  {(currentUser.is_seller || currentUser.is_provider) && <ComplianceWidget />}
                  <AccountingDocuments />
                </>
              )}
              {ownerTab === 'friends' && <FriendsSection />}
            </div>
          </div>
        )}

        {/* Friends list — shown to visitors */}
        {!isOwner && friends.length > 0 && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 mb-6" data-testid="friends-section">
            <h2 className="font-outfit font-semibold text-[#121212] mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D97736]" />{tr('Draugi', 'Friends', 'Друзья')} ({friends.length})
            </h2>
            <div className="flex flex-wrap gap-4">
              {friends.map((f) => (
                <Link key={f.id} to={`/pro/${f.id}`} className="flex flex-col items-center gap-1 w-16 group" data-testid={`friend-${f.id}`}>
                  <div className="w-12 h-12 rounded-full bg-[#4A6B53] flex items-center justify-center text-white font-semibold overflow-hidden">
                    {f.avatar_url ? <img src={resolveAssetUrl(f.avatar_url)} alt={f.name} className="w-full h-full object-cover" /> : (f.name?.charAt(0).toUpperCase())}
                  </div>
                  <span className="text-xs text-[#5C5C5C] truncate w-full text-center group-hover:text-[#D97736]">{f.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}


        {/* Portfolio */}
        {portfolio?.length > 0 && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 mb-6">
            <h2 className="font-outfit font-semibold text-[#121212] mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#D97736]" />{tr('Portfolio', 'Portfolio', 'Портфолио')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pro-portfolio-grid">
              {portfolio.map((item) => (
                <div key={item.id} className="bg-white border border-[#E5E4E0] rounded-xl overflow-hidden">
                  {item.image_url && <div className="aspect-[4/3] bg-[#F3F2EE] overflow-hidden">
                    <img src={resolveAssetUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover" /></div>}
                  <div className="p-3">
                    <p className="font-medium text-[#121212] text-sm line-clamp-1">{item.title}</p>
                    {item.description && <p className="text-xs text-[#5C5C5C] line-clamp-2 mt-1">{item.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
          <h2 className="font-outfit font-semibold text-[#121212] mb-4">
            {tr('Atsauksmes', 'Reviews', 'Отзывы')} ({reviews?.length || 0})
          </h2>
          {!reviews?.length ? (
            <p className="text-sm text-[#5C5C5C]" data-testid="pro-reviews-empty">{tr('Vēl nav atsauksmju.', 'No reviews yet.', 'Пока нет отзывов.')}</p>
          ) : (
            <div className="space-y-4" data-testid="pro-reviews-list">
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-[#F3F2EE] pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-[#121212] text-sm">{r.reviewer_name}</span>
                    <span className="inline-flex items-center gap-0.5 text-sm">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`} />
                      ))}
                    </span>
                  </div>
                  {r.comment && <p className="text-sm text-[#5C5C5C]">{r.comment}</p>}
                  {r.gig_title && <p className="text-xs text-[#9CA3AF] mt-1">“{r.gig_title}”</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {cz.banner_bottom_html && <div data-testid="pro-banner-bottom" dangerouslySetInnerHTML={{ __html: cz.banner_bottom_html }} />}
    </div>
  );
}
